import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getFeedbackList, getFeedbackItem, updateFeedback } from '../services/api';
import { Feedback } from '../services/types';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthProvider';

type StatusFilter = 'all' | 'new' | 'in_progress' | 'pending_resolution' | 'resolved' | 'wont_fix';

export default function FeedbackAdmin() {
  const { user } = useAuth();
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Selected feedback for detail view
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Update status
  const [updating, setUpdating] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');

  useEffect(() => {
    loadFeedback();
  }, [statusFilter]);

  const loadFeedback = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = statusFilter === 'all' ? {} : { status: statusFilter as Feedback['status'] };
      const data = await getFeedbackList(params);
      setFeedback(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (id: string) => {
    setLoadingDetail(true);
    try {
      const item = await getFeedbackItem(id);
      setSelectedFeedback(item);
      setReviewNotes(item.reviewNotes || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feedback detail');
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeDetail = () => {
    setSelectedFeedback(null);
    setReviewNotes('');
  };

  const handleStatusUpdate = async (newStatus: Feedback['status']) => {
    if (!selectedFeedback) return;

    setUpdating(true);
    try {
      await updateFeedback(selectedFeedback.id, {
        status: newStatus,
        reviewedBy: user?.name || 'Unknown',
        reviewNotes: reviewNotes || undefined,
      });

      // Refresh list and detail
      await loadFeedback();
      const updated = await getFeedbackItem(selectedFeedback.id);
      setSelectedFeedback(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const getStatusBadge = (status: Feedback['status']) => {
    const styles: Record<Feedback['status'], string> = {
      new: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      in_progress: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      pending_resolution: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
      wont_fix: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    };
    const labels: Record<Feedback['status'], string> = {
      new: 'New',
      in_progress: 'In Progress',
      pending_resolution: 'Pending Resolution',
      resolved: 'Resolved',
      wont_fix: "Won't Fix",
    };
    return (
      <span className={`px-2 py-0.5 rounded border text-xs ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader userEmail={user?.email} />

      <div className="flex-1 overflow-auto p-4">
        {/* Page title */}
        <div className="flex items-center gap-3 mb-4">
          <Link to="/settings/system" className="text-slate-400 hover:text-white">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 className="text-xl font-semibold text-white">User Feedback</h1>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {(['all', 'new', 'in_progress', 'pending_resolution', 'resolved', 'wont_fix'] as StatusFilter[]).map((status) => {
            const labels: Record<StatusFilter, string> = {
              all: 'All',
              new: 'New',
              in_progress: 'In Progress',
              pending_resolution: 'Pending',
              resolved: 'Resolved',
              wont_fix: "Won't Fix",
            };
            return (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1 rounded text-sm transition-colors ${
                  statusFilter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {labels[status]}
              </button>
            );
          })}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded text-red-400 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-slate-400 text-center py-8">Loading feedback...</div>
        ) : feedback.length === 0 ? (
          <div className="text-slate-500 text-center py-8">
            No feedback submissions {statusFilter !== 'all' ? `with status "${statusFilter}"` : 'yet'}
          </div>
        ) : (
          <div className="space-y-2">
            {feedback.map((item) => (
              <button
                key={item.id}
                onClick={() => openDetail(item.id)}
                className="w-full text-left p-3 bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-700 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getStatusBadge(item.status)}
                      <span className="text-slate-400 text-xs">{formatDate(item.submittedAt)}</span>
                    </div>
                    <div className="text-white text-sm truncate">{item.description}</div>
                    <div className="text-slate-500 text-xs mt-1">
                      {item.userName} • {item.pageUrl}
                    </div>
                  </div>
                  {item.hasScreenshot && (
                    <div className="text-slate-500 text-xs">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg border border-slate-700 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-white">Feedback Detail</h2>
                {getStatusBadge(selectedFeedback.status)}
              </div>
              <button
                onClick={closeDetail}
                className="text-slate-400 hover:text-white p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-4">
              {loadingDetail ? (
                <div className="text-slate-400 text-center py-8">Loading...</div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Screenshot */}
                  <div className="bg-slate-900 rounded-lg p-2">
                    {selectedFeedback.screenshot ? (
                      <img
                        src={selectedFeedback.screenshot}
                        alt="Screenshot"
                        className="w-full rounded border border-slate-700"
                      />
                    ) : (
                      <div className="text-slate-500 text-center py-8">No screenshot</div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="space-y-4">
                    {/* User info */}
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Submitted by</label>
                      <div className="text-white">{selectedFeedback.userName}</div>
                      {selectedFeedback.userEmail && (
                        <div className="text-slate-400 text-sm">{selectedFeedback.userEmail}</div>
                      )}
                    </div>

                    {/* Date */}
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Submitted at</label>
                      <div className="text-white">{formatDate(selectedFeedback.submittedAt)}</div>
                    </div>

                    {/* Page */}
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Page</label>
                      <div className="text-white font-mono text-sm">{selectedFeedback.pageUrl}</div>
                      {selectedFeedback.pageTitle && (
                        <div className="text-slate-400 text-sm">{selectedFeedback.pageTitle}</div>
                      )}
                    </div>

                    {/* Browser */}
                    {selectedFeedback.browserInfo && (
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Browser</label>
                        <div className="text-slate-400 text-sm">{selectedFeedback.browserInfo}</div>
                      </div>
                    )}

                    {/* Description */}
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Description</label>
                      <div className="text-white bg-slate-900 p-3 rounded whitespace-pre-wrap">
                        {selectedFeedback.description}
                      </div>
                    </div>

                    {/* Review section */}
                    {selectedFeedback.reviewedAt && (
                      <div className="border-t border-slate-700 pt-4">
                        <label className="text-xs text-slate-500 block mb-1">
                          Reviewed by {selectedFeedback.reviewedBy} on {formatDate(selectedFeedback.reviewedAt)}
                        </label>
                        {selectedFeedback.reviewNotes && (
                          <div className="text-slate-300 bg-slate-900 p-3 rounded text-sm">
                            {selectedFeedback.reviewNotes}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="border-t border-slate-700 pt-4">
                      <label className="text-xs text-slate-500 block mb-2">
                        {selectedFeedback.status === 'in_progress' ? 'Fix Notes (what was changed)' : 'Review Notes (optional)'}
                      </label>
                      <textarea
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                        placeholder={selectedFeedback.status === 'in_progress'
                          ? "Describe what was fixed or changed..."
                          : "Add notes about this feedback..."}
                        className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm resize-none h-20 focus:outline-none focus:border-blue-500"
                      />

                      <div className="flex gap-2 mt-3 flex-wrap">
                        {selectedFeedback.status === 'new' && (
                          <button
                            onClick={() => handleStatusUpdate('in_progress')}
                            disabled={updating}
                            className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-600 text-white text-sm rounded transition-colors"
                          >
                            Start Working
                          </button>
                        )}
                        {selectedFeedback.status === 'in_progress' && (
                          <button
                            onClick={() => handleStatusUpdate('pending_resolution')}
                            disabled={updating || !reviewNotes.trim()}
                            className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-600 text-white text-sm rounded transition-colors"
                            title={!reviewNotes.trim() ? 'Please add fix notes before sending for review' : ''}
                          >
                            Send for User Review
                          </button>
                        )}
                        {selectedFeedback.status === 'pending_resolution' && (
                          <span className="px-3 py-1.5 bg-orange-500/20 text-orange-400 text-sm rounded border border-orange-500/30">
                            Waiting for user to verify fix...
                          </span>
                        )}
                        {selectedFeedback.status === 'resolved' && (
                          <>
                            <span className="px-3 py-1.5 bg-green-500/20 text-green-400 text-sm rounded border border-green-500/30">
                              User verified fix works
                            </span>
                            <button
                              onClick={() => handleStatusUpdate('in_progress')}
                              disabled={updating}
                              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-slate-600 text-white text-sm rounded transition-colors"
                            >
                              Reopen - Fix Didn't Work
                            </button>
                          </>
                        )}
                        {!['resolved', 'wont_fix'].includes(selectedFeedback.status) && (
                          <button
                            onClick={() => handleStatusUpdate('wont_fix')}
                            disabled={updating}
                            className="px-3 py-1.5 bg-slate-600 hover:bg-slate-500 disabled:bg-slate-700 text-white text-sm rounded transition-colors"
                          >
                            Won't Fix
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
